// @ts-check
/**
 * @typedef {Object} FfCartDrawerCustomProperties
 * @property {(enable: boolean) => void} enable If true removes disable attribute
 * @property {() => Promise<void>} ffInit Adds click event for submit and close drawer
 * @property {() => {id: string}[]} getSectionsToRender
 * @property {(parsedState:{sections:{[sectionID:string]: string}|null}) => Promise<void>} renderContents
 *
 * @typedef {FfCartDrawerCustomProperties & FfDrawer} FfCartDrawer
 */
if (!customElements.get("cart-drawer"))
  customElements.define(
    "cart-drawer",
    class FfCartDrawer extends FfDrawer {
      /**
       * @type {HTMLElement[]}
       */
      ffCartItems = [];

      constructor() {
        super();
      }

      /**
       * @param {boolean} value Indicates if the submit button must be enable or disable
       */
      enable(value) {
        if (value) {
          this.querySelector("[data-ff-checkout-button]")?.removeAttribute(
            "disabled"
          );
        } else {
          this.querySelector("[data-ff-checkout-button]")?.setAttribute(
            "disabled",
            "disabled"
          );
        }
      }

      getSectionsToRender = () => {
        return [
          {
            id: "ff-cart-drawer",
          },
          {
            id: "ff-cart-icon-bubble",
          },
        ];
      };

      renderContents = async (parsedState) => {
        let sectionsFetched = parsedState?.sections || {};

        for (const section of this.getSectionsToRender()) {
          const sectionElement = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);

          let newSectionElement;
          if (sectionsFetched[section.id]) {
            newSectionElement = sectionsFetched[section.id];
          } else {
            newSectionElement = await fetch(
              //@ts-ignore
              Shopify.routes.root + "?section_id=" + section.id
            ).then((res) => res.text());
          }
          if (sectionElement) {
            if (section.id == "ff-cart-drawer") {
              this.renderSelf(newSectionElement);
            } else {
              sectionElement.innerHTML = this.getSectionInnerHTML(
                newSectionElement,
                section.selector
              );
            }
          }
        }
        if (!this.ffIsOpen) {
          this.ffOpen();
        }
      };

      getSectionInnerHTML = (html, selector = ".shopify-section") => {
        //@ts-ignore
        return new DOMParser()
          .parseFromString(html, "text/html")
          .querySelector(selector).innerHTML;
      };

      /**
       * @param {HTMLElement | Document} lineItemsContainer
       * @returns {{[lineId:string]:HTMLElement}}
       */
      ffLineItemsObj = (lineItemsContainer) => {
        /**
         * @type {HTMLElement[]}
         */
        const lineItemsEL = Array.from(
          lineItemsContainer
            .querySelector("[data-ff-line-items]")
            ?.querySelectorAll("[data-ff-line-item]") || []
        );

        return lineItemsEL.reduce((lineItemsObj, lineItemEL) => {
          const { ffLineId } = lineItemEL.dataset;

          if (ffLineId) {
            lineItemsObj[ffLineId] = lineItemEL;
          }

          return lineItemsObj;
        }, {});
      };

      renderSelf(section) {
        // Selective update only: we never replaceWith the drawer container so app blocks,
        // the checkout form and any merchant-injected widget keep their listeners/state
        // across cart transitions (including empty <-> filled). Visibility of empty vs
        // filled slots is driven by the data-ff-cart-state attribute + CSS.
        const sectionDOM = new DOMParser().parseFromString(
          section,
          "text/html"
        );

        const newCartDrawer = sectionDOM.querySelector("#ff-cart-drawer");
        const newCartState = newCartDrawer?.dataset.ffCartState || "empty";
        this.dataset.ffCartState = newCartState;

        const newTotalsHTML =
          sectionDOM.querySelector("[data-ff-totals]")?.innerHTML;
        const totalsEl = this.querySelector("[data-ff-totals]");
        if (totalsEl && typeof newTotalsHTML === "string") {
          totalsEl.innerHTML = newTotalsHTML;
        }

        const newCartItemsCount = sectionDOM.querySelector(
          "[data-ff-line-items-count]"
        )?.textContent;
        if (newCartItemsCount != null) {
          this.querySelectorAll("[data-ff-line-items-count]").forEach(
            (cartItemsCountEL) => {
              cartItemsCountEL.textContent = newCartItemsCount;
            }
          );
        }

        const drawerItems = this.querySelector("[data-ff-line-items]");
        if (drawerItems) {
          /**
           * @type {HTMLElement[]}
           */
          const newDrawerItems = Array.from(
            sectionDOM
              .querySelector("[data-ff-line-items]")
              ?.querySelectorAll("[data-ff-line-item]") || []
          );

          const drawerItemsObj = this.ffLineItemsObj(this);

          newDrawerItems.forEach((newLineItemEL) => {
            const { ffLineId } = newLineItemEL.dataset;

            if (ffLineId && drawerItemsObj[ffLineId]) {
              const oldLineEL = drawerItemsObj[ffLineId];
              //This content purpose is to avoid line image update and preserve event listeners
              const newLineContentEl = newLineItemEL.querySelector(
                "ff-cart-item-content"
              );
              const oldLineContentEl = oldLineEL.querySelector(
                "ff-cart-item-content"
              );
              if (newLineContentEl && oldLineContentEl) {
                oldLineContentEl.replaceWith(newLineContentEl);
              }

              delete drawerItemsObj[ffLineId];
            } else {
              drawerItems.prepend(newLineItemEL);
            }
          });

          //Remove line items no longer in cart
          Object.values(drawerItemsObj).forEach((remainingLineEl) =>
            remainingLineEl.remove()
          );

          if (newDrawerItems.length > 0) {
            drawerItems.scrollBy({
              top: -drawerItems.scrollHeight,
              behavior: "smooth",
            });
          }
        }

        this.enable(newCartState === "filled");
      }

      // This method only exists to have compatibility with product-form
      setActiveElement() {}
    }
  );

if (!customElements.get("ff-cart-item-content")) {
  customElements.define(
    "ff-cart-item-content",
    class FfCartItem extends HTMLElement {
      /**
       * @type {string}
       * The id associated to the cart line
       */
      lineId;

      /**
       * @type {HTMLDivElement}
       * The cart drawer element parent of this drawer item
       */
      cartDrawer;

      /**
       * @type {number}
       * Store the current quantity for the line, just in case a quantity update couldn't be done
       */
      prevQty;

      constructor() {
        super();

        this.ffLineId = this.dataset.ffLineId;
        this.ffLineKey = this.dataset.ffLineKey;

        /**
         * @type {HTMLInputElement}
         */
        //@ts-ignore
        this.input = this.querySelector("input");
        /**
         * @type {HTMLButtonElement}
         */
        //@ts-ignore
        this.buttonMinus = this.querySelector("[data-ff-quantity-minus]");
        /**
         * @type {HTMLButtonElement}
         */
        //@ts-ignore
        this.buttonPlus = this.querySelector("[data-ff-quantity-plus]");
        /**
         * @type {FfCartDrawer}
         */
        //@ts-ignore
        this.ffCartDrawer = this.closest("cart-drawer");
        this.prevQty = parseInt(this.input.value);

        this.buttonMinus.addEventListener("click", async () => {
          const isLoading = this.classList.contains("loading");

          if (isLoading) return;

          this.input.stepDown();
          const value = parseInt(this.input.value);
          if (value > 0) {
            if (parseInt(this.input.dataset.min) > value) {
              this.input.value = this.input.dataset.min;
            }
          } else if (value < 0) {
            this.input.value = 0;
          }

          await this.changeQty(this.input.value);
        });

        this.buttonPlus.addEventListener("click", async () => {
          const isLoading = this.classList.contains("loading");

          if (isLoading) return;

          this.input.stepUp();
          if (parseInt(this.input.dataset.min) > parseInt(this.input.value)) {
            this.input.value = this.input.dataset.min;
          }

          await this.changeQty(this.input.value);
        });

        this.input.addEventListener("change", async () => {
          const newQty = parseInt(this.input.value || "0");

          await this.changeQty(newQty);
        });

        this.validateQtyRules();
      }

      validateQtyRules = () => {
        const value = parseInt(this.input.value);
        if (this.input.min && value <= parseInt(this.input.min)) {
          this.buttonMinus.disabled = true;
        } else {
          this.buttonMinus.disabled = false;
        }
        if (this.input.max && value >= parseInt(this.input.max)) {
          this.buttonPlus.disabled = true;
        } else {
          this.buttonPlus.disabled = false;
        }
      };

      /**
       * @param {number} newQty The new qty for the line
       */
      async changeQty(newQty) {
        const addQty = newQty - this.dataset.ffInitialQuantity;
        
        let data = {
          // @ts-ignore
          sections: this.ffCartDrawer
            .getSectionsToRender()
            .map((section) => section.id),
        };

        if (addQty > 0) {
          var url = window["Shopify"].routes.root + "cart/add.js";
          data.items = [
            {
              id: this.ffLineId,
              quantity: addQty,
            },
          ];
        } else {
          var url = window["Shopify"].routes.root + "cart/update.js";
          data.updates = { [this.ffLineKey]: newQty <= 0 ? 0 : newQty };
        }
        this.classList.add("loading");

        this.input.setAttribute("disabled", "disabled");

        this.ffCartDrawer.enable(false);

        try {
          const res = await fetch(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(data),
          });

          const result = await res.json();

          if (result?.status === 422) {
            this.input.value = this.prevQty.toString();
          } else {
            this.prevQty = newQty;

            await this.ffCartDrawer.renderContents(result);
          }
        } catch (error) {
          console.log(error);

          this.input.value = this.prevQty.toString();
        } finally {
          this.classList.remove("loading");

          this.input.removeAttribute("disabled");

          this.validateQtyRules();

          this.ffCartDrawer.enable(true);
        }
      }
    }
  );
}
